import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormProvider, RHFTextField } from '../../../components';
import { VisibilityIcon, VisibilityOffIcon } from '../../../icons';
import { BlankLayout } from '../../../layouts';
import { setRequiresPasswordChange } from '../../../redux/reducers/auth.reducer';
import { AuthService, ToastService } from '../../../services';

type FormData = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const ChangePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    const storedEmail = localStorage.getItem('pendingPasswordChangeEmail');
    if (!storedEmail) {
      navigate('/login');
      return;
    }
    setEmail(storedEmail);
  }, [navigate]);

  const formSchema = Yup.object().shape({
    oldPassword: Yup.string().required(t('validationErrors.requiredField')),
    newPassword: Yup.string()
      .required(t('validationErrors.requiredField'))
      .min(8, t('auth.passwordMinLength')),
    confirmPassword: Yup.string()
      .required(t('validationErrors.requiredField'))
      .oneOf([Yup.ref('newPassword')], t('auth.passwordsDoNotMatch')),
  });

  const defaultValues = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const methods = useForm<FormData>({
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const onSubmit = (form: FormData) => {
    if (!email) {
      ToastService.error(t('auth.emailRequired'));
      navigate('/login');
      return;
    }

    setLoading(true);
    AuthService.updatePassword(email, form.oldPassword, form.newPassword, false)
      .then(() => {
        dispatch(setRequiresPasswordChange(false));
        localStorage.removeItem('pendingPasswordChangeEmail');
        ToastService.success(t('auth.passwordChangedSuccessfully'));
        navigate('/');
      })
      .catch((err) => {
          ToastService.showHttpError(err, 'toast.updatePasswordFailed');
      })
      .finally(() => setLoading(false));
  };

  if (!email) {
    return null;
  }

  return (
    <BlankLayout className="flex-center flex-col p-4 sm:p-8 lg:p-18">
      <div className="w-full max-w-86">
        <h1 className="mb-10 text-center text-34p font-bold text-typo1">{t('auth.changePassword')}</h1>
        <p className="mb-6 text-center text-typo2">{t('auth.changePasswordRequired')}</p>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <RHFTextField
            name="oldPassword"
            type={showOldPassword ? 'text' : 'password'}
            label={t('auth.oldPassword')}
            placeholder={t('auth.oldPassword')}
            endAdornment={
              <div
                className="cursor-pointer text-black/54"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </div>
            }
          />
          <RHFTextField
            className="mt-4"
            name="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            label={t('auth.newPassword')}
            placeholder={t('auth.newPassword')}
            endAdornment={
              <div
                className="cursor-pointer text-black/54"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </div>
            }
          />
          <RHFTextField
            className="mt-4"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            label={t('auth.confirmPassword')}
            placeholder={t('auth.confirmPassword')}
            endAdornment={
              <div
                className="cursor-pointer text-black/54"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </div>
            }
          />
          <Button
            type="submit"
            className="mt-10"
            typoClass="typo-button-lg"
            fullWidth
            disabled={!isValid}
            loading={loading}
          >
            {t('auth.changePassword')}
          </Button>
        </FormProvider>
      </div>
    </BlankLayout>
  );
};

